import css from 'styled-jsx/css'
import { LoginStep } from '@ui/components/contact/enum'
import { Dispatch, SetStateAction, FormEvent, useEffect, useRef, useState } from 'react'
import { PinField } from '@ui/components/shared/PinField/pin-field'
import { getLocalObject, setLocalObject, displayUSPhoneFormatted } from '@ui/helpers'
import Loading from '@ui/components/shared/Loading'
import { useAuthService } from '@ui/services/auth/auth.service'
import getConfig from 'next/config'
import { useRedirect } from '@ui/hooks'

type CodeFormProps = {
  nextStep: (step?: any) => void
  prevStep: string
  searchForm: { phone: string; email: string }
  setDisplayStatus: Dispatch<SetStateAction<boolean>>
}

function onTypeCode(code: string, ref?: HTMLInputElement) {
  if (ref && code) {
    ref.value = code.toUpperCase()
  }
}

export const EnterCodeForm: React.FC<CodeFormProps> = ({
  nextStep,
  setDisplayStatus,
  searchForm
}) => {
  const redirect = useRedirect()
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const pinRef = useRef<HTMLInputElement[]>(null)
  const [code, setCode] = useState<string>('')

  const [countdown, setCountdown] = useState<number>(60)
  const { login } = useAuthService(getConfig().publicRuntimeConfig.shophelpBasePathApi)
  const AUTH_LOGIN = 'AUTH_LOGIN'
  const localAuth = getLocalObject(AUTH_LOGIN)
  const IS_AUTHEN_TWO_STEP = 'IS_AUTHEN_TWO_STEP'
  const CODE_LENGTH = 6

  function clearPinField() {
    setCode('')
    for (let idx = 0; idx < CODE_LENGTH; idx++) {
      const ele = document.getElementById(`code_idx_${idx}`) as HTMLInputElement
      const eleMobile = document.getElementById(`code_idx_mobile_${idx}`) as HTMLInputElement
      if (ele) {
        ele.value = ''
      }
      if (eleMobile) {
        eleMobile.value = ''
      }
    }
  }

  async function resentCode() {
    setLoading(true)
    let body: Types.LoginModel
    {
      searchForm.phone != ''
        ? (body = {
            phone: searchForm.phone,
            send_sms: true
          })
        : (body = {
            email: searchForm.email,
            send_sms: false
          })
    }
    const result = await login(body, false).then((data) => {
      clearPinField()
      setLoading(false)
      if (data === undefined) {
        setError(`Can not resend code now`)
        return false
      }
      setError(``)
      return true
    })
    if (result) {
      setCountdown(60)
    }
  }

  function backToLogin() {
    window.localStorage.removeItem(AUTH_LOGIN)
    nextStep(LoginStep.EMAIL)
  }

  async function onLoginByCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError('')
    const body: Types.LoginModel =
      searchForm.email || localAuth?.email
        ? {
            email: searchForm.email || localAuth?.email,
            code
          }
        : {
            phone: searchForm.phone || localAuth?.phone,
            code
          }
    setLoading(true)
    const result = await login(body).then((data) => {
      if (data === undefined) {
        setLoading(false)
        setError(`Invalid code`)
        setDisplayStatus(true)
        return false
      }
      setDisplayStatus(false)
      return true
    })
    if (result) {
      setLocalObject(IS_AUTHEN_TWO_STEP, true)
      if (body.phone) {
        redirect(`/order`, { routerOptions: { scroll: false }, query: { ref: 'login-phone-code' } })
      } else {
        redirect(`/order`, { routerOptions: { scroll: false }, query: { ref: 'login-email-code' } })
      }
    }
  }

  useEffect(() => {
    let timer: any
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000)

    return () => clearTimeout(timer)
  }, [countdown])

  return (
    <>
      <style jsx>{compStyle}</style>
      <style jsx>{pinFieldStyleMobile}</style>
      <style jsx>{pinFieldStyleDesktop}</style>
      <style jsx>{horizontalLineStyle}</style>
      {loading && <Loading isFullWidth />}
      <div className="login-form ml-auto mr-auto has-text-centered">
        <h1 className="login-form__title mt-8 mb-4">Verification code</h1>
        <p
          id={`enter-code-description`}
          className="login-form__content is-align-items-center is-justify-content-center"
        >
          Please enter the code send to your{' '}
          {searchForm.phone || localAuth?.phone ? 'phone' : 'email'}
        </p>
        <b>
          {searchForm.phone || localAuth?.phone
            ? displayUSPhoneFormatted(searchForm.phone) || displayUSPhoneFormatted(localAuth?.phone)
            : searchForm.email || localAuth?.email}
        </b>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            onLoginByCode(e)
          }}
        >
          <div className="field mt-8 mb-8">
            <div className="pincodes mt-4 mb-2">
              <PinField
                ref={pinRef}
                length={6}
                type="text"
                inputMode="numeric"
                className={`pincode__input pincode__dot ${error && 'is-invalid'}`}
                autoComplete="one-time-code"
                onResolveKey={(code: string, ref?: HTMLInputElement) =>
                  onTypeCode(code, ref || undefined)
                }
                onChange={(code: string) => {
                  setCode(code)
                }}
                disabled={loading}
              />
            </div>
            {error && error !== 'true' && <p className="is-danger">{error}</p>}
          </div>
          <p className="has-text-centered question">Didn&apos;t receive code?</p>
          {countdown == 0 ? (
            <>
              <a
                className="is-link-custom"
                onClick={() => {
                  resentCode()
                }}
              >
                Resend code
              </a>
              {' | '}
              <a
                className={`is-link-custom back-to-login-sh`}
                onClick={() => {
                  backToLogin()
                }}
              >
                Send to {searchForm.phone != '' || localAuth?.phone ? 'email' : 'phone'}
              </a>
            </>
          ) : (
            <p className="login-form__content has-text-centered">
              Resend code in:
              <span className="ml-3" style={{ color: '#f2994a' }}>
                {countdown}s
              </span>
            </p>
          )}
          <div className="field mt-8 mb-8">
            <div className={`control`}>
              <button
                id="verify-code-btn"
                type="submit"
                className="button is-primary button--cta is-fullwidth-mobile"
                style={{ width: '100%' }}
                disabled={code.length < 6}
              >
                Confirm
              </button>
            </div>
            <h2>
              <span>OR</span>
            </h2>
          </div>
          <a
            className={`is-link-custom  switch-to-ord-num-link`}
            onClick={() => {
              nextStep(LoginStep.ORDER)
            }}
          >
            Enter Order number instead
          </a>
        </form>
      </div>
    </>
  )
}

const compStyle = css`
  p {
    color: var(--indigo-7);
    &.is-danger {
      color: #ff0000;
    }
  }

  .question {
    font-size: 13px;
    color: var(--grey);
  }

  .countdown {
    border: 2px solid var(--indigo-1);
    box-sizing: border-box;
    border-radius: var(--round-4);
    width: 200px;
    height: 48px;

    &.is-resent {
      border: 2px solid var(--secondary-50);
      color: var(--secondary-100);
      cursor: pointer;

      span {
        font-weight: 500;
      }

      &:hover {
        background: var(--indigo-2);
        span {
          font-weight: 700;
        }
      }
    }
  }

  .button {
    width: 200px;
    height: 48px;
  }

  .login-form {
    background: var(--white-100);
    box-shadow: 2px 4px 40px rgba(161, 178, 203, 0.25);
    border-radius: var(--round-4);
    padding: 2.5rem;
    max-width: 450px;

    font-size: 15px;
    line-height: 32px;

    &__title {
      font-size: 21px;
      font-weight: 600;
    }

    &__content {
      color: var(--indigo-7);
    }

    .icon.is-left {
      color: var(--indigo-7);
      &::after {
        content: '';
        position: absolute;
        border: 1px solid var(--indigo-3);
        height: 16px;
        right: 0;
      }
    }
    .control.has-icons-left .input {
      padding-left: 3rem;
    }
    .input {
      ::-webkit-input-placeholder {
        /* Chrome/Opera/Safari */
        color: var(--indigo-6);
        opacity: 0.3;
      }
      ::-moz-placeholder {
        /* Firefox 19+ */
        color: var(--indigo-6);
        opacity: 0.3;
      }
      :-ms-input-placeholder {
        /* IE 10+ */
        color: var(--indigo-6);
        opacity: 0.3;
      }
      :-moz-placeholder {
        /* Firefox 18- */
        color: var(--indigo-6);
        opacity: 0.3;
      }
      &:focus {
        border: 1px solid #007aff;
        box-shadow: none;
      }
      &:hover {
        box-shadow: none;
      }
    }
  }

  .btn-submit {
    padding: 8px 24px;
    background: var(--white-100);
    border: 2px solid var(--secondary-50);
    box-sizing: border-box;
    border-radius: var(--round-4);
    color: var(--secondary-100);

    &:hover {
      background: var(--indigo-2);
      font-weight: 700;
    }
  }

  .is-link-custom {
    cursor: pointer;
    color: #2d9cdb;
    font-weight: 400;
  }
`

const horizontalLineStyle = css`
  h2 {
    width: 100%;
    text-align: center;
    border-bottom: 2px solid gray;
    line-height: 0.1em;
    margin: 30px 0 20px;
  }

  h2 span {
    background: #fff;
    padding: 0 20px;
    font-weight: 700;
    color: gray;
  }
`

const pinFieldStyleMobile = css.global`
  @media screen and (max-width: 768px) {
    .pincode__input {
      width: 30px;
      font-size: 24px;
      line-height: 40px;
      color: var(--gray-8);
      padding: 1px 1px;

      border: 1px solid rgb(204, 204, 204);
      border-radius: 0.3rem;
      margin: 0.2rem;
      height: 3.5rem;
      outline: none;
      text-align: center;
      transition-duration: 250ms;
      transition-property: background, color, border, box-shadow, transform;

      &:focus {
        outline: none;
      }
      &:not(:placeholder-shown) {
        text-align: center;
      }
      caret-color: var(--secondary-100);
    }
    .is-invalid {
      border: 2px solid #ff0000;
    }
    .pincode__dot {
      ::-webkit-input-placeholder {
        /* Chrome/Opera/Safari */
        color: var(--indigo-4);
        padding-left: 5px;
      }
      ::-moz-placeholder {
        /* Firefox 19+ */
        color: var(--indigo-4);
        padding-left: 5px;
      }
      :-ms-input-placeholder {
        /* IE 10+ */
        color: var(--indigo-4);
        padding-left: 5px;
      }
      :-moz-placeholder {
        /* Firefox 18- */
        color: var(--indigo-4);
        padding-left: 5px;
      }
    }
  }
`

const pinFieldStyleDesktop = css.global`
  @media screen and (min-width: 769px) {
    .pincode__input {
      width: 40px;
      font-size: 24px;
      line-height: 40px;
      color: var(--gray-8);

      border: 1px solid rgb(204, 204, 204);
      border-radius: 0.3rem;
      margin: 0.2rem;
      height: 3.5rem;
      outline: none;
      text-align: center;
      transition-duration: 250ms;
      transition-property: background, color, border, box-shadow, transform;

      &:focus {
        outline: none;
      }
      &:not(:placeholder-shown) {
        text-align: center;
      }
      caret-color: var(--secondary-100);
    }
    .is-invalid {
      border: 2px solid #ff0000;
    }
    .pincode__dot {
      ::-webkit-input-placeholder {
        /* Chrome/Opera/Safari */
        color: var(--indigo-4);
        padding-left: 5px;
      }
      ::-moz-placeholder {
        /* Firefox 19+ */
        color: var(--indigo-4);
        padding-left: 5px;
      }
      :-ms-input-placeholder {
        /* IE 10+ */
        color: var(--indigo-4);
        padding-left: 5px;
      }
      :-moz-placeholder {
        /* Firefox 18- */
        color: var(--indigo-4);
        padding-left: 5px;
      }
    }
  }
`
